import {
  BANNER_INSIDE_LIST_CMS,
  BANNER_INSIDE_LIST_PROMO,
  BANNER_ON_PAGE_CMS,
  BANNER_ON_PAGE_PROMO,
  HERO_BANNER_CMS,
  HERO_BANNER_PROMO,
} from 'lib/datoCms/constants';
import React, { useEffect } from 'react';
import { conditionsToVisualizeBanner, gtag, isTruthy } from 'utility/functions';
import { useRouter, withRouter } from 'next/router';

import { BANNER_TYPE_PORTRAIT } from 'components/banner/utils/constant';
import { BannerBasic } from 'components/banner/BannerBasic';
import { BannerHero } from 'components/banner/bannerHero';
import { BannerLandscape } from 'components/banner/bannerLandscape';
import { Category } from 'types/categoryEnum';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { InternalMenu } from 'components/internalMenu';
import { LearnMore } from 'components/learnMore';
import { NextPage } from 'next';
import { Skeleton } from 'components/skeleton';
import { SlotArchive } from 'features/slotArchive';
import { SlotCardType } from 'components/cards/slotCard';
import { SlotCategoryPageQuery } from 'lib/datoCms/queries/getAllSlotCategoryPages';
import { SuggestionCarousel } from 'components/carousels/suggestionCarousel';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { WithRouterProps } from 'next/dist/client/with-router';
import { handleBanner } from 'lib/datoCms/utils';
import { resetFilters } from 'features/slotArchive/slotArchiveSlice';
import { useAppDispatch } from 'lib/centralStore';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useGetMightLikesByList } from 'hooks/useGetAllMightLikes';
import { useGetMightLikesQuery } from 'features/api/apiSlice';
import { useGetPageBannersQuery } from 'features/api/promoEngineApiSlice';

type Props = {
  slug: string;
  slotCategoryPage: SlotCategoryPageQuery['allSlotCategoryPages'];
  choseFromSnaiCardProps: Array<SlotCardType>;
} & WithRouterProps;

const PageContent: NextPage<Props> = ({ slug, slotCategoryPage, choseFromSnaiCardProps }: Props) => {
  const {
    id,
    title,
    heroBanner,
    internalMenu,
    slotListModularContent,
    bannerInsideList,
    bannerOnPage,
    learnMore,
    tabsWithAccordion,
  } = slotCategoryPage ?? {};

  // query promo engine
  const {
    isLoading: isLoadingPromoEngine,
    isError: isErrorPromoEngine,
    isUninitialized: isUninitializedPromoEngine,
    data: promoEngine,
  } = useGetPageBannersQuery(String(id));
  const { data: mightLikesResponse, isLoading } = useGetMightLikesQuery(slug);
  const mightLikesList = useGetMightLikesByList(mightLikesResponse?.cardList);

  const labelSearch = useGetLabelByKey('search')!;
  const labelSlot = useGetLabelByKey('slot')!;
  const dispatch = useAppDispatch();
  const dynamicRoute = useRouter().asPath;

  useEffect(() => {
    dispatch(resetFilters());
  }, [dynamicRoute, dispatch]);

  if (isLoadingPromoEngine && isUninitializedPromoEngine) {
    return <p>promoEngine is loading</p>;
  }

  return (
    <React.Fragment>
      {isTruthy(title?.length) && (
        <HeaderTitleWithSearch
          title={labelSlot}
          titleHighlighted={title}
          category={Category.Slot}
          labelSearch={labelSearch}
          cardChoseFromSnai={choseFromSnaiCardProps}
          hasPaddingLeft
        />
      )}
      {conditionsToVisualizeBanner(slotCategoryPage, {
        type: HERO_BANNER_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      }) && <BannerHero banners={promoEngine!.heroBannerList} />}

      {conditionsToVisualizeBanner(slotCategoryPage, {
        type: HERO_BANNER_CMS,
      }) && <BannerHero banners={handleBanner(heroBanner[0].banners!)} />}

      {isTruthy(internalMenu?.length) && (
        <InternalMenu
          items={internalMenu[0].linkList}
          gtagAction={(tab: string) => gtag({ section: 'Slot', detail: `Click ${tab}`, event: 'Tab Click' }, true)}
        />
      )}

      {isLoading ? (
        <Skeleton hasPadding rows={3} />
      ) : (
        <React.Fragment>
          <SlotArchive slotListModularContent={slotListModularContent}>
            {isTruthy(bannerInsideList?.length) &&
              (conditionsToVisualizeBanner(slotCategoryPage, {
                type: BANNER_INSIDE_LIST_CMS,
              }) ? (
                <BannerBasic
                  dataBanner={handleBanner(bannerInsideList![0]?.banners!)?.[0]}
                  bannerType={BANNER_TYPE_PORTRAIT}
                />
              ) : (
                conditionsToVisualizeBanner(slotCategoryPage, {
                  type: BANNER_INSIDE_LIST_PROMO,
                  data: promoEngine,
                  hasError: isErrorPromoEngine,
                }) && <BannerBasic dataBanner={promoEngine!.pageBannerList[0]} bannerType={BANNER_TYPE_PORTRAIT} />
              ))}
          </SlotArchive>

          {isTruthy(bannerOnPage?.length) &&
            conditionsToVisualizeBanner(slotCategoryPage, {
              type: BANNER_ON_PAGE_CMS,
            }) && <BannerLandscape data={handleBanner(bannerOnPage![0].banners!)?.[0]} />}

          {conditionsToVisualizeBanner(slotCategoryPage, {
            type: BANNER_ON_PAGE_PROMO,
            data: promoEngine,
            hasError: isErrorPromoEngine,
          }) && <BannerLandscape data={promoEngine!.pageBannerList[0]} />}
        </React.Fragment>
      )}

      {isTruthy(mightLikesList?.length) && <SuggestionCarousel listItem={mightLikesList!} />}
      {learnMore && <LearnMore title={learnMore.title} links={learnMore.links} />}
      {tabsWithAccordion && <TabsWithAccordion {...tabsWithAccordion} />}
    </React.Fragment>
  );
};
export default withRouter(PageContent);
